import jwt_decode from 'jwt-decode';
import { RouteList } from 'routes';
import ApiUtil from '../utils/ApiUtil';

/**
 * Interface for authentication credentials
 */
interface Credentials {
  refresh_token: string;
  access_token: string;
}

/**
 * Interface for token response structure
 */
interface Tokens {
  access_token: string | undefined;
  refresh_token: string | undefined;
}

/**
 * Interface for OTP verification response
 */
interface VerifyOtpResponse {
  tokens?: Tokens;
  token?: string;
}

/**
 * Interface for password update data
 */
interface PasswordData {
  password: string;
  confirmPassword: string;
}

/**
 * Interface for email existence check response
 */
interface EmailCheckResponse {
  exist?: boolean;
}

/**
 * Retrieves access token from sessionStorage
 * @returns Access token string or null if not found
 */
const getAccessToken = (): string | null => {
  const credentials = JSON.parse(sessionStorage.getItem('credentials') as string);
  if (credentials?.access_token) {
    return credentials.access_token;
  }
  return null;
};

/**
 * Extracts user role from JWT token stored in localStorage
 * @returns User role string or null if not found/invalid
 */
const getRole = (): string | null => {
  const credentials = JSON.parse(sessionStorage.getItem('credentials') as string);
  if (credentials?.access_token) {
    const { role }: { role: string } = jwt_decode(credentials.access_token);

    if (role?.length > 0) {
      return role;
    }
    return null;
  }
  return null;
};

/**
 * Checks if user is currently logged in by validating JWT token expiration
 * @returns True if user is logged in and token is valid, false otherwise
 */
const loginCheck = (): boolean => {
  const credentials = JSON.parse(sessionStorage.getItem('credentials') as string);
  if (credentials?.access_token) {
    const { exp }: { exp: number } = jwt_decode(credentials.access_token);
    if (Date.now() <= exp * 1000) {
      return true;
    }
    sessionStorage.removeItem('credentials');
  }
  return false;
};

/**
 * Stores authentication credentials in localStorage
 * Preserves existing refresh token if available (since refresh API doesn't return it)
 * @param credentials - The credentials object containing access and refresh tokens
 * @returns Promise resolving to true on successful storage
 */
const storeAccessToken = async (credentials: Credentials): Promise<boolean> => {
  let localCredentials = JSON.parse(
    sessionStorage.getItem('credentials') as string,
  );

  // eslint-disable-next-line @typescript-eslint/no-misused-promises, no-async-promise-executor
  return await new Promise(async (resolve, reject) => {
    try {
      if (
        Boolean(localCredentials?.refresh_token) &&
        Boolean(localCredentials?.access_token)
      ) {
        /* The refresh_token api doesn't return refresh_token. in that case we keep
         * Existing refresh token
         */
        localCredentials = {
          ...localCredentials,
          ...credentials,
        };
        sessionStorage.setItem('credentials', JSON.stringify(localCredentials));
      } else {
        sessionStorage.setItem('credentials', JSON.stringify(credentials));
      }

      setTimeout(() => {
        resolve(true);
      }, 1000);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Removes all authentication data from localStorage
 * @returns Promise that resolves when cleanup is complete
 */
const removeAccessToken = async (): Promise<void> => {
  await new Promise((resolve, reject) => {
    try {
      sessionStorage.clear();
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Authenticates user with email and password
 * @param email - User's email address
 * @param password - User's password
 * @returns Promise resolving to authentication response
 * @throws Error if login fails
 */
async function login(email: string, password: string): Promise<unknown> {
  try {
    return await ApiUtil.postWithoutToken('admin/auth/login', {
      email,
      password,
    });
  } catch (error) {
    console.log('Error during login.');
    throw error;
  }
}

/**
 * Sends OTP (One-Time Password) to user's email or phone
 * @param value - Email address or phone number
 * @param type - Type of OTP ('email' or 'phone')
 * @returns Promise resolving to OTP sending response
 */
const sendOtp = async (value: string, type: string, token: string): Promise<unknown> =>
  await ApiUtil.postWithTempToken('admin/auth/sendOtp', { value, type }, token);

/**
 * Verifies OTP and returns authentication tokens or email token
 * @param value - Email address or phone number
 * @param type - Type of OTP ('email' or 'phone')
 * @param otp - The OTP code to verify
 * @returns Promise resolving to token string or undefined
 */
const verifyOtp = async (
  value: string,
  type: string,
  otp: string,
  token: string
): Promise<string | undefined> => {
  const res = (await ApiUtil.postWithTempToken('admin/auth/verifyOtp', {
    value,
    type,
    otp,
  }, token)) as VerifyOtpResponse; // Add a type assertion to cast the response to VerifyOtpResponse

  // Store access and refresh tokens if provided
  if (res?.tokens) {
    await storeAccessToken(res.tokens as Credentials);
  }
  // Return email token for OTP verification
  return res?.token;
};

/**
 * Logs out user by clearing credentials and redirecting to sign-in page
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const logout = async (): Promise<any> => {
  const localCredentials = JSON.parse(
    sessionStorage.getItem('credentials') as string,
  );
  if (Boolean(localCredentials?.refresh_token) &&
    Boolean(localCredentials?.access_token)) { await logoutEmp(); }
  void removeAccessToken();
  sessionStorage.clear();
  window.history.replaceState(null, '', RouteList.SignIn);
  window.location.reload();
};

/**
 * Logs out user due to token error and redirects to token error page
 */
const logoutTokenError = (): void => {
  void removeAccessToken();
  sessionStorage.clear();
  window.history.replaceState(null, '', RouteList.TokenError);
  window.location.reload();
};

/**
 * Checks if an email address already exists in the system
 * @param email - Email address to check
 * @returns Promise resolving to email existence response
 * @throws Error if check fails
 */
const emailCheck = async (
  email: string,
): Promise<EmailCheckResponse | unknown> => {
  try {
    return await ApiUtil.postWithoutToken('admin/auth/emailAlreadyExists', {
      email,
    });
  } catch (error) {
    throw new Error(`something went to wrong!. ${String(error)}`);
  }
};

/**
 * Retrieves email details for a given email address
 * @param email - Email address to get details for
 * @returns Promise resolving to email details
 * @throws Error if request fails
 */
const emailDetails = async (email: string): Promise<unknown> => {
  try {
    return await ApiUtil.postWithToken('emailDetails', {
      email,
    });
  } catch (error) {
    throw new Error(`something went to wrong!. ${String(error)}`);
  }
};

/**
 * Initiates forgot password process by sending reset email
 * @param email - Email address for password reset
 * @returns Promise resolving to forgot password response
 * @throws Error if request fails
 */
const forgotPassword = async (email: string): Promise<unknown> => {
  try {
    return await ApiUtil.postWithoutToken('admin/auth/forgotpassword', {
      email,
    });
  } catch (error) {
    throw new Error(`something went to wrong!. ${String(error)}`);
  }
};

/**
 * Updates user password using employee ID and reset token
 * @param empId - Employee ID
 * @param token - Password reset token
 * @param data - Password data containing new password and confirmation
 * @returns Promise resolving to password update response
 * @throws Error if update fails
 */
const updatePassword = async (
  empId: string,
  token: string,
  data: PasswordData,
): Promise<unknown> => {
  try {
    return await ApiUtil.postWithoutToken(
      `admin/auth/updatepassword/${empId}/${token}`,
      {
        password: data?.password,
        confirmPassword: data?.confirmPassword,
      },
    );
  } catch (error) {
    throw new Error(`something went to wrong!. ${String(error)}`);
  }
};

/**
 * Verifies both email and phone OTP for customer verification
 * @param emailOtp - Email OTP code
 * @param phoneOtp - Phone OTP code
 * @param email - Customer's email address
 * @param phone - Customer's phone number
 * @returns Promise resolving to verification response
 */
const verifyCustomerEmailAndPhoneOtp = async (
  emailOtp: string,
  phoneOtp: string,
  email: string,
  phone: string,
): Promise<string | unknown | undefined> => {
  const res = await ApiUtil.postWithoutToken(
    'admin/auth/verify-customer-email-and-phone-otp',
    {
      emailOtp,
      phoneOtp,
      email,
      phone,
    },
  );
  return res;
};

const logoutEmp = async (): Promise<unknown> => {
  try {
    return await ApiUtil.postWithToken('logout');
  } catch (error) {
    throw new Error(`something went to wrong!. ${String(error)}`);
  }
};

export {
  loginCheck,
  storeAccessToken,
  removeAccessToken,
  login,
  sendOtp,
  verifyOtp,
  logout,
  emailCheck,
  emailDetails,
  forgotPassword,
  updatePassword,
  getRole,
  getAccessToken,
  logoutTokenError,
  verifyCustomerEmailAndPhoneOtp,
};
